import { Model } from '@nozbe/watermelondb';

import Entry from '../../Entry';
import EventContact from '../../EventContact';
import EventContactReminder from '../../EventContactReminder';
import EventHorse from '../../EventHorse';
import EventUser from '../../EventUser';
import {
    DBServiceOptionsWithImagesAndValidation,
    EntryDBServiceOptions,
} from '../../../../types/dbService';
import { EntryModel } from '../../../../types/Entries';
import { EntryProcedureModel } from '../../../../types/EntryProcedure';
import { EntryProductModel } from '../../../../types/EntryProduct';
import { EventContactModel } from '../../../../types/EventContact';
import { EventContactReminderModel } from '../../../../types/EventContactReminder';
import { EventHorseModel } from '../../../../types/EventHorse';
import { EventModel, PreparedDataOnUpdate } from '../../../../types/Events';
import { EventUserModel } from '../../../../types/EventUser';
import { InventoryChangeModel } from '../../../../types/InventoryChange';
import { isFuture } from '../../../../utils/date';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import { mapRecords } from '../common/mapRecords';

const prepareDeleteEvent = async ({
    eventElement,
}: {
    eventElement: EventModel;
}) => {
    return eventElement.prepareMarkAsDeleted();
};

const deleteHorsesRelatedData = async ({
    eventElement,
    eventId,
    serviceOptions,
}: {
    eventElement: EventModel;
    eventId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
}) => {
    let eventHorsesToDelete: EventHorseModel[] = [];

    const eventHorses = await eventElement.horses.fetch();

    if (eventHorses.length > 0) {
        const eventHorseService = new EventHorse(serviceOptions);
        const eventHorsesToRemove = await eventHorseService.getByParam(
            'event_id',
            eventId,
        );

        eventHorsesToDelete = eventHorsesToRemove.map((eventHorse) =>
            eventHorse.prepareMarkAsDeleted(),
        );
    }

    return { eventHorsesToDelete };
};

const deleteContactsRelatedData = async ({
    eventElement,
    eventId,
    serviceOptions,
}: {
    eventElement: EventModel;
    eventId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
}) => {
    let eventContactsToDelete: EventContactModel[] = [];

    const eventContacts = await eventElement.contacts.fetch();

    if (eventContacts.length > 0) {
        const eventContactService = new EventContact(serviceOptions);
        const eventContactsToRemove = await eventContactService.fetchByParam(
            'event_id',
            eventId,
        );

        eventContactsToDelete = eventContactsToRemove.map((eventContact) =>
            eventContact.prepareMarkAsDeleted(),
        );
    }

    return { eventContactsToDelete };
};

const deleteContactRemindersRelatedData = async ({
    eventId,
    serviceOptions,
}: {
    eventId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
}) => {
    let eventContactRemindersToDelete: EventContactReminderModel[] = [];

    const eventContactRemindersService = new EventContactReminder(
        serviceOptions,
    );
    const eventContactReminders = await eventContactRemindersService.getByParam(
        'event_id',
        eventId,
    );

    if (eventContactReminders.length > 0) {
        eventContactRemindersToDelete = eventContactReminders.map(
            (eventContactReminder) =>
                eventContactReminder.prepareMarkAsDeleted(),
        );
    }

    return { eventContactRemindersToDelete };
};

const splitEntryDeleteBatch = (
    deleteBatch: Model[],
): {
    entryProceduresToDelete: EntryProcedureModel[];
    entryProductsToDelete: EntryProductModel[];
    inventoryChangesToAdd: InventoryChangeModel[];
    entriesToDelete: EntryModel[];
} => {
    const entryProceduresToDelete: EntryProcedureModel[] = [];
    const entryProductsToDelete: EntryProductModel[] = [];
    const inventoryChangesToAdd: InventoryChangeModel[] = [];
    const entriesToDelete: EntryModel[] = [];

    for (const record of deleteBatch) {
        switch (record.table) {
            case 'entry_procedures':
                entryProceduresToDelete.push(record as EntryProcedureModel);
                break;
            case 'entry_products':
                entryProductsToDelete.push(record as EntryProductModel);
                break;
            case 'inventory_changes':
                inventoryChangesToAdd.push(record as InventoryChangeModel);
                break;
            case 'entries':
                entriesToDelete.push(record as EntryModel);
                break;
        }
    }

    return {
        entryProceduresToDelete,
        entryProductsToDelete,
        inventoryChangesToAdd,
        entriesToDelete,
    };
};

const deleteEntriesRelatedData = async ({
    eventElement,
    eventId,
    userId,
    serviceOptions,
}: {
    eventElement: EventModel;
    eventId: string;
    userId: string;
    serviceOptions: EntryDBServiceOptions;
}) => {
    let entryProceduresToDelete: EntryProcedureModel[] = [];
    let entryProductsToDelete: EntryProductModel[] = [];
    let inventoryChangesToAdd: InventoryChangeModel[] = [];
    let entriesToDelete: EntryModel[] = [];
    let relatedEntriesToUpdate: EntryModel[] = [];

    const entryService = new Entry(serviceOptions);
    const relatedEntries = await entryService.getByParam('event_id', eventId);

    if (relatedEntries.length > 0) {
        if (isFuture(eventElement.startsTime)) {
            const deleteBatches = await Promise.all(
                relatedEntries.map((entry) =>
                    entryService.prepareDeleteById(entry.id, userId),
                ),
            );

            for (const deleteBatch of deleteBatches) {
                const split = splitEntryDeleteBatch(deleteBatch);
                entryProceduresToDelete.push(...split.entryProceduresToDelete);
                entryProductsToDelete.push(...split.entryProductsToDelete);
                inventoryChangesToAdd.push(...split.inventoryChangesToAdd);
                entriesToDelete.push(...split.entriesToDelete);
            }
        } else {
            relatedEntriesToUpdate = await Promise.all(
                relatedEntries.map((entry) =>
                    entry.prepareUpdate((relatedEntry) => {
                        relatedEntry.eventId = '';
                        relatedEntry.loggedTime = eventElement.startsTime;
                    }),
                ),
            );
        }
    }

    return {
        entryProceduresToDelete,
        entryProductsToDelete,
        inventoryChangesToAdd,
        entriesToDelete,
        relatedEntriesToUpdate,
    };
};

const deleteEventUsersRelatedData = async ({
    eventId,
    serviceOptions,
}: {
    eventId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
}) => {
    const eventUserService = new EventUser(serviceOptions);
    const eventUsers = await eventUserService.getByParam('event_id', eventId);

    const eventUsersToDelete = eventUsers.map((eventUser) =>
        eventUser.prepareMarkAsDeleted(),
    );

    return { eventUsersToDelete };
};

const mapDeleteHorsesRelatedData = (
    data: Awaited<ReturnType<typeof deleteHorsesRelatedData>>,
    preparedData: PreparedDataOnUpdate[],
) => {
    const newPreparedData: PreparedDataOnUpdate[] = [...preparedData];

    newPreparedData.push(
        ...mapRecords(data.eventHorsesToDelete, 'event_horses', 'delete'),
    );

    return newPreparedData;
};

const mapDeleteContactsRelatedData = (
    data: Awaited<ReturnType<typeof deleteContactsRelatedData>>,
    preparedData: PreparedDataOnUpdate[],
) => {
    const newPreparedData: PreparedDataOnUpdate[] = [...preparedData];

    newPreparedData.push(
        ...mapRecords(data.eventContactsToDelete, 'event_contacts', 'delete'),
    );

    return newPreparedData;
};

const mapDeleteContactRemindersRelatedData = (
    data: Awaited<ReturnType<typeof deleteContactRemindersRelatedData>>,
    preparedData: PreparedDataOnUpdate[],
) => {
    const newPreparedData: PreparedDataOnUpdate[] = [...preparedData];

    newPreparedData.push(
        ...mapRecords(
            data.eventContactRemindersToDelete,
            'event_contact_reminders',
            'delete',
        ),
    );

    return newPreparedData;
};

const mapDeleteEntriesRelatedData = (
    data: Awaited<ReturnType<typeof deleteEntriesRelatedData>>,
    preparedData: PreparedDataOnUpdate[],
) => {
    const newPreparedData: PreparedDataOnUpdate[] = [...preparedData];

    newPreparedData.push(
        ...mapRecords(
            data.entryProceduresToDelete,
            'entry_procedures',
            'delete',
        ),
    );
    newPreparedData.push(
        ...mapRecords(data.entryProductsToDelete, 'entry_products', 'delete'),
    );
    newPreparedData.push(
        ...mapRecords(data.inventoryChangesToAdd, 'inventory_changes', 'add'),
    );
    newPreparedData.push(
        ...mapRecords(data.entriesToDelete, 'entries', 'delete'),
    );
    newPreparedData.push(
        ...mapRecords(data.relatedEntriesToUpdate, 'entries', 'update'),
    );

    return newPreparedData;
};

const mapDeleteEventUsersRelatedData = (
    data: Awaited<ReturnType<typeof deleteEventUsersRelatedData>>,
    preparedData: PreparedDataOnUpdate[],
) => {
    const newPreparedData: PreparedDataOnUpdate[] = [...preparedData];

    newPreparedData.push(
        ...mapRecords(data.eventUsersToDelete, 'event_users', 'delete'),
    );

    return newPreparedData;
};

type DeleteRelatedDataBuckets = {
    eventHorsesToDelete: EventHorseModel[];
    eventContactsToDelete: EventContactModel[];
    eventContactRemindersToDelete: EventContactReminderModel[];
    entryProceduresToDelete: EntryProcedureModel[];
    entryProductsToDelete: EntryProductModel[];
    inventoryChangesToAdd: InventoryChangeModel[];
    entriesToDelete: EntryModel[];
    relatedEntriesToUpdate: EntryModel[];
    eventUsersToDelete: EventUserModel[];
};

const logDeleteDBActions = ({
    eventElement,
    buckets,
    logDBAction,
    tableName,
}: {
    eventElement: EventModel;
    buckets: DeleteRelatedDataBuckets;
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    const {
        eventHorsesToDelete,
        eventContactsToDelete,
        eventContactRemindersToDelete,
        entryProceduresToDelete,
        entryProductsToDelete,
        entriesToDelete,
        relatedEntriesToUpdate,
    } = buckets;

    if (eventElement) {
        logDBAction({
            message: 'Delete event',
            modelName: tableName,
            payload: eventElement,
        });
    }

    if (eventHorsesToDelete.length > 0) {
        logDBAction({
            message: 'Delete event - delete event horses',
            modelName: tableName,
            payload: eventHorsesToDelete,
        });
    }

    if (eventContactsToDelete.length > 0) {
        logDBAction({
            message: 'Delete event - delete event contacts',
            modelName: tableName,
            payload: eventContactsToDelete,
        });
    }

    if (eventContactRemindersToDelete.length > 0) {
        logDBAction({
            message: 'Delete event - delete event contact reminders',
            modelName: tableName,
            payload: eventContactRemindersToDelete,
        });
    }

    const relatedEntriesToDelete = [
        ...entryProceduresToDelete,
        ...entryProductsToDelete,
        ...entriesToDelete,
    ];

    if (relatedEntriesToDelete.length > 0) {
        logDBAction({
            message: 'Delete event - delete related entries',
            modelName: tableName,
            payload: relatedEntriesToDelete,
        });
    }

    if (relatedEntriesToUpdate.length > 0) {
        logDBAction({
            message: 'Delete event - update related entries',
            modelName: tableName,
            payload: relatedEntriesToUpdate,
        });
    }
};

export {
    prepareDeleteEvent,
    deleteHorsesRelatedData,
    deleteContactsRelatedData,
    deleteContactRemindersRelatedData,
    deleteEntriesRelatedData,
    deleteEventUsersRelatedData,
    mapDeleteHorsesRelatedData,
    mapDeleteContactsRelatedData,
    mapDeleteContactRemindersRelatedData,
    mapDeleteEntriesRelatedData,
    mapDeleteEventUsersRelatedData,
    logDeleteDBActions,
};

export type { DeleteRelatedDataBuckets };
